import { isCachedReadDenial } from './cached-read-denial.mjs';

// Host-only, document-scoped read seeds admitted in the existing bootstrap.
// This map never persists across documents, acts as authority, or handles an
// effect. A refreshed seed is delivered only after its real server admission.
const reads = new Set(['connections.read']);
function canonical(value) {
  if (Array.isArray(value)) return '[' + value.map(canonical).join(',') + ']';
  if (value && typeof value === 'object')
    return (
      '{' +
      Object.keys(value)
        .sort()
        .map((k) => JSON.stringify(k) + ':' + canonical(value[k]))
        .join(',') +
      '}'
    );
  return JSON.stringify(value);
}
const keyFor = (operation, input) => canonical([operation, input]);
export function createCachedReadSession({
  seed,
  load,
  assertCurrent = () => {},
  onDenied = () => {},
}) {
  const entries = new Map(),
    listeners = new Set();
  let active = true,
    epoch = 0,
    revision = 0,
    materialRevision = 0,
    pending = null;
  let view = Object.freeze(seed?.view ?? { fetchedAt: null, status: 'idle' });
  for (const entry of seed?.reads ?? []) {
    if (!reads.has(entry.operation)) continue;
    const key = keyFor(entry.operation, entry.input);
    if (entries.has(key)) throw TypeError('invalid_cached_read');
    entries.set(key, { ...structuredClone(entry), ready: true });
  }
  const notify = () => {
    for (const listener of listeners)
      try {
        listener(view);
      } catch {
        /* An observer cannot retain other listeners. */
      }
  };
  const check = async (ticket) => {
    if (!active || ticket !== epoch)
      throw Object.assign(Error('context_changed'), { code: 'scope_invalidated' });
    await assertCurrent();
    if (!active || ticket !== epoch)
      throw Object.assign(Error('context_changed'), { code: 'scope_invalidated' });
  };
  const invalidate = () => {
    active = false;
    epoch++;
    entries.clear();
    view = Object.freeze({ fetchedAt: null, status: 'failed' });
    notify();
  };
  // Only what M3 names: the connected source refusing a Refresh. A 409 (access withdrawn) still retires.
  const sourceUnavailable = (error) =>
    error?.code === 'resource_connection_unavailable' && error.statusCode !== 409;
  const retireDenied = (error, keepShown = false) => {
    if (keepShown) {
      // Keep only material already rendered by the App. Unconsumed seeds
      // and replies from the prior epoch cannot disclose protected values;
      // retain their inputs solely for the next freshly admitted attempt.
      epoch++;
      materialRevision++;
      for (const entry of entries.values()) {
        delete entry.value;
        entry.ready = false;
      }
      return;
    }
    if (isCachedReadDenial(error)) {
      invalidate();
      onDenied(error);
    }
  };
  const failureFor = (result) =>
    Object.assign(Error('cached_read_failed'), {
      code: result.error?.code ?? 'UNAVAILABLE',
      statusCode: result.error?.statusCode,
      cacheDenied: isCachedReadDenial(result.error),
    });
  function keep(key, operation, input, result, { target, refresh }) {
    if (!result.view || result.value?.ok === false) return false;
    const { key: cacheKey, generation, fetchedAt, status, stale } = result.view;
    target.set(key, {
      operation,
      input: structuredClone(input),
      value: structuredClone(result.value),
      key: cacheKey,
      generation,
      fetchedAt,
      status,
      stale,
      ready: refresh,
    });
    if (!refresh) {
      materialRevision++;
      // Before a host refresh this is the document's initial read. Once a
      // refreshed batch exists, a reply alone cannot establish that the
      // mounted App adopted it: one-shot records may still show old values.
      // Keep the last known displayed age until a new document adopts its
      // admitted seed. Never invent a painted/consumed acknowledgement.
      view = Object.freeze({
        ...view,
        fetchedAt: revision === 0 ? fetchedAt : view.fetchedAt,
        status,
      });
      notify();
    }
    return true;
  }
  function accept(key, operation, input, result, { target, refresh }) {
    if (result.value?.ok === false) {
      const failure = failureFor(result.value);
      if (!refresh) retireDenied(failure);
      if (refresh) throw failure;
      return result.value;
    }
    if (!keep(key, operation, input, result, { target, refresh }) && refresh)
      throw Object.assign(Error('incomplete_refresh'), {
        code: 'view_cache_material_incomplete',
      });
    return result.value;
  }
  const usable = (hit, refresh) => Boolean(hit?.ready && !refresh);
  const modeFor = (_hit, refresh) => (refresh ? 'refresh' : 'open');
  async function one(operation, input, refresh = false, signal, target = entries) {
    const ticket = epoch;
    await check(ticket);
    const key = keyFor(operation, input),
      hit = target.get(key);
    if (usable(hit, refresh)) {
      hit.ready = false;
      return structuredClone(hit.value);
    }
    try {
      const result = await load(operation, structuredClone(input), {
        cacheMode: modeFor(hit, refresh),
        ...(signal ? { signal } : {}),
      });
      await check(ticket);
      const accepted = accept(key, operation, input, result, {
        target,
        refresh,
      });
      return accepted;
    } catch (error) {
      // A Refresh the source refuses keeps the view already shown (no new bytes are served); an ordinary read keeps
      // the canonical retirement.
      if (ticket === epoch) retireDenied(error, refresh && sourceUnavailable(error));
      throw error;
    }
  }
  const effect = async (operation, input, options) => {
    try {
      const result = await load(
        operation,
        input,
        options?.signal ? { signal: options.signal } : undefined,
      );
      if (result.value?.ok === false) retireDenied(failureFor(result.value));
      return result.value;
    } catch (error) {
      retireDenied(error);
      throw error;
    }
  };
  return Object.freeze({
    // A caller's abort signal reaches the real read; a cache hit needs none and host refreshes pass none.
    request(operation, input, options) {
      if (!reads.has(operation)) return effect(operation, input, options);
      return one(operation, input, false, options?.signal);
    },
    // Connected reads are one-shot projections. Live collection stores stay
    // on their canonical collections.live path and never enter this session.
    getSnapshot: () => view,
    subscribe(listener) {
      listeners.add(listener);
      listener(view);
      return () => listeners.delete(listener);
    },
    refresh() {
      if (pending) return pending;
      const ticket = epoch,
        original = [...entries.values()],
        originalRevision = materialRevision,
        staged = new Map(entries);
      // A new attempt drops the previous failure's reason; only this attempt's failure may set one.
      const { reason: _previous, ...shown } = view;
      view = Object.freeze({ ...shown, status: 'refreshing' });
      notify();
      pending = (async () => {
        try {
          // No provider timer/poll. Only this explicit host action invokes the
          // original reads; at most eight source acquisitions run at once.
          for (let offset = 0; offset < original.length; offset += 8)
            await Promise.all(
              original
                .slice(offset, offset + 8)
                .map((x) => one(x.operation, x.input, true, undefined, staged)),
            );
          await check(ticket);
          if (materialRevision !== originalRevision)
            throw Object.assign(Error('refresh_superseded'), {
              code: 'view_cache_material_incomplete',
            });
          // Publish only the complete admitted batch. Failed/late reads stay
          // private, and a newer read or confirmed effect keeps its marker.
          for (const [key, value] of staged) entries.set(key, value);
          view = Object.freeze({
            // This revision makes admitted data available to real SDK
            // subscribers. It is not proof of the values actually shown.
            fetchedAt: view.fetchedAt,
            status: 'idle',
            revision: ++revision,
          });
          notify();
        } catch (error) {
          if (active) {
            view = Object.freeze({
              ...view,
              status: 'failed',
              ...(sourceUnavailable(error) ? { reason: 'connection' } : {}),
            });
            notify();
          }
          throw error;
        } finally {
          pending = null;
        }
      })();
      return pending;
    },
    invalidate,
  });
}
